import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as Inputs from "../../common/hook-form/index";
import Spinner from "../../common/Spinner";
import {
  useCreatePunchCard,
  usePunchCard,
  useUpdatePunchCard,
} from "../../../service/api";
import { ProductsInPack } from "./PunchCardProducts";
import { PunchCardType } from "../../../service/api-response/PunchCards";
import { routes } from "../../../appRoutingHelpers";
import { GoBackLinkButton } from "../../../components/GoBackLinkButton";
import { useSelector } from "react-redux";
import * as authSelectors from "./../../auth/authSelectors";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { ButtonAction } from "../../../components/NewButtons";

export const PunchCardUpsert: React.FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();

  const selectedOrganizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const methods = useForm<PunchCardType>();
  const { handleSubmit, formState, register, reset } = methods;

  const {
    mutateAsync: createProductPack,
    isLoading: isCreatingProductPack,
  } = useCreatePunchCard();
  const {
    mutateAsync: updateProductPack,
    isLoading: isUpdatingProductPack,
  } = useUpdatePunchCard();

  const {
    data: punchCard,
    isLoading: punchCardLoading,
    error: punchCardError,
  } = usePunchCard(selectedOrganizationKeyName, id);

  const isEdit = !!id;

  useEffect(() => {
    if (isEdit && punchCard) {
      reset(punchCard);
    }
  }, [isEdit, punchCard, reset]);

  useHandleFetchError(punchCardError);

  if (isEdit && punchCardLoading) {
    return (
      <div className="card-footer text-center">
        <Spinner />
      </div>
    );
  }

  const onSubmit = async (punchCard: PunchCardType) => {
    const newValue = {
      ...punchCard,
      organizationKeyName: selectedOrganizationKeyName,
      products: punchCard.products.map(({ id, amount }) => {
        return { id, amount };
      }),
    };
    try {
      if (isEdit) {
        await updateProductPack(newValue);
      } else {
        await createProductPack(newValue);
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
    navigate(routes.punchCards(selectedOrganizationKeyName));
  };

  return (
    <div className="container mt-5">
      <GoBackLinkButton
        to={routes.punchCards(selectedOrganizationKeyName)}
        text="Back to all punch cards"
      />
      {/* @ts-ignore */}
      <FormProvider {...methods}>
        <div className="row">
          <div className="col-12">
            <form className="card">
              <div className="card-header">
                <h3 className="card-title">
                  {isEdit ? <>Punch Card Edit</> : <>New Punch Card</>}
                </h3>
              </div>
              <div className="card-body">
                <Inputs.InputFormField
                  errors={formState.errors}
                  register={register}
                  label="Name"
                  name="name"
                />
                <ProductsInPack />
              </div>
              <div className="card-footer text-right">
                <ButtonAction
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isUpdatingProductPack || isCreatingProductPack}
                >
                  {isUpdatingProductPack || isCreatingProductPack ? (
                    <ActionSpinner />
                  ) : (
                    "Save"
                  )}
                </ButtonAction>
              </div>
            </form>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
